import * as Y from 'yjs';

export type BoardObjectKind = 'text' | 'note' | 'rect' | 'stroke';

export interface BoardPoint {
  x: number;
  y: number;
}

export interface BoardObject {
  id: string;
  kind: BoardObjectKind;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  text?: string;
  points?: BoardPoint[];
}

export interface BoardEditMarker {
  editId: string;
  originSeat: 'floor' | 'tv';
  capturedEpochMs: number;
  clockSynchronized: boolean;
}

export interface BoardTypes {
  objects: Y.Map<BoardObject>;
  order: Y.Array<string>;
  editMarkers: Y.Map<BoardEditMarker>;
}

const id = (): string => crypto.randomUUID();

export function boardTypes(doc: Y.Doc): BoardTypes {
  return {
    objects: doc.getMap<BoardObject>('board:objects'),
    order: doc.getArray<string>('board:order'),
    editMarkers: doc.getMap<BoardEditMarker>('board:editMarkers'),
  };
}

export function boardObjects(board: BoardTypes): BoardObject[] {
  return board.order.toArray().map((objectId) => board.objects.get(objectId)).filter((object): object is BoardObject => Boolean(object));
}

export function transactBoard(
  doc: Y.Doc,
  board: BoardTypes,
  update: () => void,
  originSeat: 'floor' | 'tv' = 'floor',
  capturedEpochMs = Date.now(),
  clockSynchronized = false,
): BoardEditMarker {
  const marker: BoardEditMarker = { editId: id(), originSeat, capturedEpochMs, clockSynchronized };
  doc.transact(() => {
    update();
    board.editMarkers.set(marker.editId, marker);
  }, originSeat);
  return marker;
}

export function addBoardObject(
  doc: Y.Doc,
  board: BoardTypes,
  object: Omit<BoardObject, 'id'>,
  capturedEpochMs = Date.now(),
  clockSynchronized = false,
): BoardObject {
  const created = { ...object, id: id() };
  transactBoard(doc, board, () => {
    board.objects.set(created.id, created);
    board.order.push([created.id]);
  }, 'floor', capturedEpochMs, clockSynchronized);
  return created;
}

export function updateBoardObject(
  doc: Y.Doc,
  board: BoardTypes,
  objectId: string,
  patch: Partial<BoardObject>,
  capturedEpochMs = Date.now(),
  clockSynchronized = false,
): BoardObject | undefined {
  const current = board.objects.get(objectId);
  if (!current) return undefined;
  const next = { ...current, ...patch, id: current.id, kind: current.kind };
  transactBoard(doc, board, () => board.objects.set(objectId, next), 'floor', capturedEpochMs, clockSynchronized);
  return next;
}

export function seedBoard(doc: Y.Doc, board: BoardTypes): void {
  if (board.order.length > 0 || board.objects.size > 0) return;
  doc.transact(() => {
    const objects: BoardObject[] = [
      { id: id(), kind: 'text', x: 84, y: 74, width: 700, height: 90, color: '#f2eee5', text: 'The mirror loop' },
      { id: id(), kind: 'note', x: 110, y: 205, width: 255, height: 190, color: '#f4c66d', text: 'Edit here. The same state lights the real TV and the TV in the room.' },
      { id: id(), kind: 'rect', x: 420, y: 220, width: 410, height: 230, color: '#7ba58d' },
      { id: id(), kind: 'text', x: 455, y: 286, width: 335, height: 100, color: '#10231b', text: 'ONE DOCUMENT\nTWO SCREENS' },
    ];
    for (const object of objects) board.objects.set(object.id, object);
    board.order.push(objects.map((object) => object.id));
  }, 'seed');
}
